import { AdjustIconAction, AdjustIconActionGroup, AdjustNote, AdjustSlider } from '../AdjustControlPrimitives.component'
import { apparentStickerRotation, stickerRotationFromApparent } from '../adjustStickers'

import { AdjustSticker } from '../adjust.types'
import ContentCopyOutlinedIcon from '@mui/icons-material/ContentCopyOutlined'
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline'
import { FC } from 'react'
import FlipIcon from '@mui/icons-material/Flip'
import { Stack } from '@mui/material'
import { useAdjustEditor } from '../AdjustEditor.context'
import { useTranslation } from 'react-i18next'

/** Rotate, fade, flip, duplicate or delete the sticker selected on the photo */
export const SelectedStickerControls: FC<{ sticker: AdjustSticker, disabled?: boolean }> = ({ sticker, disabled = false }) => {
  const { t } = useTranslation(['photo_studio_editing'])
  const { doc, apply, commit, setSelectedStickerId } = useAdjustEditor()
  const rotation = Math.round(apparentStickerRotation(sticker, doc.crop))
  const update = (patch: Partial<AdjustSticker>, historyKey?: string) => apply({ type: 'updateSticker', id: sticker.id, patch }, historyKey)

  const duplicate = () => {
    const id = crypto.randomUUID()
    const offset = Math.max(sticker.width, sticker.height) * 0.15
    apply({ type: 'addSticker', sticker: { ...sticker, id, x: sticker.x + offset, y: sticker.y + offset } })
    setSelectedStickerId(id)
  }

  const remove = () => {
    apply({ type: 'removeSticker', id: sticker.id })
    setSelectedStickerId(null)
  }

  return (
    <Stack gap="1.2rem">

      <AdjustSlider
        label={t('adjust.stickers.rotate')}
        value={rotation}
        valueLabel={`${rotation}°`}
        min={-180}
        max={180}
        origin={0}
        disabled={disabled}
        onChange={(value) => update({ rotation: stickerRotationFromApparent(value, doc.crop) }, `sticker:rotate:${sticker.id}`)}
        onCommit={commit}
        onReset={() => update({ rotation: stickerRotationFromApparent(0, doc.crop) })}
      />

      <AdjustSlider
        label={t('adjust.stickers.opacity')}
        value={Math.round(sticker.opacity * 100)}
        valueLabel={`${Math.round(sticker.opacity * 100)}%`}
        min={10}
        max={100}
        disabled={disabled}
        onChange={(value) => update({ opacity: value / 100 }, `sticker:opacity:${sticker.id}`)}
        onCommit={commit}
      />

      <AdjustIconActionGroup columns={3}>

        <AdjustIconAction icon={<FlipIcon />} label={t('adjust.stickers.flip')} disabled={disabled} onClick={() => update({ flipX: !sticker.flipX })} />

        <AdjustIconAction icon={<ContentCopyOutlinedIcon />} label={t('adjust.stickers.duplicate')} disabled={disabled} onClick={duplicate} />

        <AdjustIconAction icon={<DeleteOutlineIcon />} label={t('adjust.stickers.delete')} disabled={disabled} onClick={remove} danger />

      </AdjustIconActionGroup>

      <AdjustNote>{t('adjust.stickers.move_hint')}</AdjustNote>

    </Stack>
  )
}
